import { useState } from "react";
import { useGetApiRequests, triggerNotification } from "../../../common/CommonExport";
import { Employee, KpiQuestionaire, KpiPayload, ApiError } from "../types";

interface UseAddKpiProps {
  monthYear: string;
  orgId?: number | string;
  reportingManagerId?: number | string;
  selectedDesignationId?: number;
  onSuccess: () => void;
}

export const useAddKpi = ({ monthYear, orgId, reportingManagerId, selectedDesignationId, onSuccess }: UseAddKpiProps) => {
  const [isAddKpiModalOpen, setIsAddKpiModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [questionaireList, setQuestionaireList] = useState<KpiQuestionaire[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [bulkKpiPayload, setBulkKpiPayload] = useState<KpiPayload[]>([]);
  const [addedEmployeeIds, setAddedEmployeeIds] = useState<number[]>([]);
  const [questionnaireInput, setQuestionnaireInput] = useState<Record<number, { target?: number; actual?: number; weight?: number }>>({});

  const getQuestionaireListApi = useGetApiRequests("questionaireList", "GET");
  const addKpiUserWeightApi = useGetApiRequests("addKpiUserWeight", "POST");

  const fetchQuestionaireList = async (designationId: number) => {
    setModalLoading(true);
    try {
      const pathParams = { id: designationId };
      const [response] = await Promise.all([getQuestionaireListApi(null, {}, pathParams), new Promise((resolve) => setTimeout(resolve, 2000))]);

      if (response && "data" in response && response.data.status.success === "Success") {
        const data = response.data.data || [];
        setQuestionaireList(data);
      }
    } catch (error) {
      console.error("Failed to fetch questionaire list", error);
      const err = error as ApiError;
      const errorMessage = err.response?.data?.status?.message || err.response?.data?.message || err.message || "An error occurred while fetching the questionnaire list.";
      triggerNotification("error", errorMessage, "", "topRight");
    } finally {
      setModalLoading(false);
    }
  };

  const handleOpenAddKpiModal = async (employee: Employee) => {
    setIsAddKpiModalOpen(true);
    setSelectedEmployee(employee);

    const existingData = bulkKpiPayload.filter((item) => item.employee.employeeId === employee.employeeId);
    if (existingData.length > 0) {
      const inputMap: Record<number, { target?: number; actual?: number; weight?: number }> = {};
      existingData.forEach((item) => {
        inputMap[item.kpiQuestionaire.kpiQuestionaireId] = {
          target: item.totalTargets,
          weight: item.weightScore,
        };
      });
      setQuestionnaireInput(inputMap);
    } else {
      setQuestionnaireInput({});
    }

    if (selectedDesignationId) {
      await fetchQuestionaireList(selectedDesignationId);
    }
  };

  const handleQuestionaireInputChange = (id: number, field: "target" | "weight" | "actual", value: number | null) => {
    let finalValue = value;
    if (field === "target" && finalValue !== null && finalValue > 999) {
      finalValue = 999;
    }

    if (field === "weight" && finalValue !== null) {
      const questionCallback = questionaireList.find((q) => q.kpiQuestionaireId === id);
      if (questionCallback && finalValue > questionCallback.kpiWeightage) {
        return;
      }
    }

    setQuestionnaireInput((prev) => {
      let finalValue = value;
      const currentInput = prev[id] || {};

      if (field === "actual" && finalValue !== null && currentInput.target !== undefined && finalValue > currentInput.target) {
        finalValue = currentInput.target;
      }

      const next = {
        ...prev,
        [id]: { ...currentInput, [field]: finalValue },
      };

      // Also handle case where target is changed and it's now less than current actual
      if (field === "target" && finalValue !== null) {
        const currentActual = next[id].actual;
        if (currentActual !== undefined && currentActual > finalValue) {
          next[id].actual = finalValue;
        }
      }

      const question = questionaireList.find((q) => q.kpiQuestionaireId === id);
      const input = next[id];

      if (question && input?.target != null && input?.actual != null && input.target !== 0) {
        next[id] = {
          ...input,
          weight: (input.actual / input.target) * question.kpiWeightage,
        };
      } else if (input) {
        next[id] = { ...input, weight: undefined };
      }

      return next;
    });
  };

  const isFormValid =
    questionaireList.length > 0 &&
    questionaireList.every((q) => {
      const input = questionnaireInput[q.kpiQuestionaireId];
      return input && input.target != null && input.actual != null;
    });

  const handleCloseAddKpiModal = () => {
    setIsAddKpiModalOpen(false);
    setSelectedEmployee(null);
    setQuestionaireList([]);
    setQuestionnaireInput({});
  };

  const handleSaveKpiModal = async () => {
    if (!selectedEmployee) return;

    const newEntries: KpiPayload[] = questionaireList.map((q) => {
      const input = questionnaireInput[q.kpiQuestionaireId];
      return {
        kpiQuestionaire: { kpiQuestionaireId: q.kpiQuestionaireId },
        employee: { employeeId: selectedEmployee.employeeId },
        monthYear: monthYear,
        totalTargets: input?.target || 0,
        weightScore: input?.weight || 0,
      };
    });

    // setBulkKpiPayload((prev) => {
    //   const filtered = prev.filter((item) => item.employee.employeeId !== selectedEmployee.employeeId);
    //   return [...filtered, ...newEntries];
    // });

    setAddedEmployeeIds((prev) => {
      if (!prev.includes(selectedEmployee.employeeId)) {
        return [...prev, selectedEmployee.employeeId];
      }
      return prev;
    });

    if (newEntries.length === 0) {
      triggerNotification("warning", "No KPI data to save.", "", "topRight");
      return;
    }
    if (!orgId || !reportingManagerId) {
      triggerNotification("error", "Missing organisation or user ID.", "", "topRight");
      return;
    }
    setSubmitLoading(true);
    try {
      const queryParams = { organisationId: orgId };
      const pathParams = { id: reportingManagerId };

      const response = await addKpiUserWeightApi(newEntries, queryParams, pathParams);

      if (response && "data" in response && response.data.status.success === "Success") {
        triggerNotification("success", response.data.status.message || "All KPIs submitted successfully!", "", "topRight");
        setBulkKpiPayload([]);
        setAddedEmployeeIds([]);
        onSuccess();
      } else {
        const errorMsg = (response as ApiError)?.data?.status?.message || "Failed to submit KPIs.";
        triggerNotification("error", errorMsg, "", "topRight");
      }
    } catch (error) {
      console.error("Bulk submit error", error);
      const err = error as ApiError;
      const errorMessage = err.response?.data?.status?.message || err.response?.data?.message || err.message || "An error occurred while submitting KPIs.";
      triggerNotification("error", errorMessage, "", "topRight");
    } finally {
      setSubmitLoading(false);
    }

    handleCloseAddKpiModal();
  };

  const handleSubmitBulkKpi = async () => {
    if (bulkKpiPayload.length === 0) {
      triggerNotification("warning", "No KPI data to save.", "", "topRight");
      return;
    }

    if (!orgId || !reportingManagerId) {
      triggerNotification("error", "Missing organisation or user ID.", "", "topRight");
      return;
    }

    setSubmitLoading(true);
    try {
      const queryParams = { organisationId: orgId };
      const pathParams = { id: reportingManagerId };

      const response = await addKpiUserWeightApi(bulkKpiPayload, queryParams, pathParams);

      if (response && "data" in response && response.data.status.success === "Success") {
        triggerNotification("success", response.data.status.message || "All KPIs submitted successfully!", "", "topRight");
        setBulkKpiPayload([]);
        setAddedEmployeeIds([]);
        onSuccess();
      } else {
        const errorMsg = (response as ApiError)?.data?.status?.message || "Failed to submit KPIs.";
        triggerNotification("error", errorMsg, "", "topRight");
      }
    } catch (error) {
      console.error("Bulk submit error", error);
      const err = error as ApiError;
      const errorMessage = err.response?.data?.status?.message || err.response?.data?.message || err.message || "An error occurred while submitting KPIs.";
      triggerNotification("error", errorMessage, "", "topRight");
    } finally {
      setSubmitLoading(false);
    }
  };

  return {
    isAddKpiModalOpen,
    selectedEmployeeAdd: selectedEmployee,
    questionaireList,
    modalLoadingAdd: modalLoading,
    handleOpenAddKpiModal,
    handleCloseAddKpiModal,
    questionnaireInput,
    handleQuestionaireInputChange,
    isFormValid,
    handleSaveKpiModal,
    handleSubmitBulkKpi,
    addedEmployeeIds,
    bulkKpiPayload,
    submitLoading,
  };
};
